import { useRef, useState } from 'react'
import type { ScheduleRule } from '../api/types'
import { de } from '../i18n/de'

export const ALL_DAYS = 0x7f
export const MINUTES_PER_DAY = 1440

export interface ScheduleBand {
  rules: ScheduleRule[]
  color: string
  label?: string
  /** Hintergrundband (Gruppenplan im Vergleichsmodus). */
  ghost?: boolean
  /** Diagonal schraffiert: durch den Gruppenplan abgeschnitten. */
  clipped?: boolean
}

interface Block {
  day: number
  from: number
  to: number
  band: ScheduleBand
  index: number
}

export function formatMinute(minute: number): string {
  const hours = Math.floor(minute / 60)
  const minutes = minute % 60
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

export function dayMaskText(mask: number): string {
  const days: string[] = []
  for (let day = 0; day < 7; day++) {
    if (mask & (1 << day)) days.push(de.schedule.days[day]!)
  }
  return days.join(', ')
}

export function describeRule(rule: ScheduleRule): string {
  return `${dayMaskText(rule.dayMask)} ${formatMinute(rule.startMinute)}–${formatMinute(rule.endMinute)}`
}

export function alwaysRule(): ScheduleRule {
  return { dayMask: ALL_DAYS, startMinute: 0, endMinute: MINUTES_PER_DAY }
}

function toBlocks(bands: ScheduleBand[]): Block[] {
  const blocks: Block[] = []
  bands.forEach((band, index) => {
    for (const rule of band.rules) {
      for (let day = 0; day < 7; day++) {
        if (rule.dayMask & (1 << day)) {
          blocks.push({ day, from: rule.startMinute, to: rule.endMinute, band, index })
        }
      }
    }
  })
  return blocks
}

/**
 * Wochenplan-Gitter. Drei Zustände, wie in design/06-designsystem.md beschrieben:
 *
 * - Lesemodus: mehrere Bänder überlagert, keine Interaktion.
 * - Bearbeitungsmodus (`onChange`): ziehen legt ein Fenster an, Klick auf einen Block
 *   löscht ihn.
 * - Vergleichsmodus: Bänder mit `ghost` liegen als Gruppenplan im Hintergrund, `clipped`
 *   markiert, was der Gruppenplan abschneidet.
 *
 * Das Gitter ist Komfort. Der verlässliche Pfad ist die Liste darunter (ScheduleRuleList),
 * die denselben Zustand bearbeitet und tastaturbedienbar ist.
 */
export function ScheduleGrid({
  bands, rowHeight = 22, fromHour = 0, toHour = 24, onChange, snapMinutes = 15,
}: {
  bands: ScheduleBand[]
  rowHeight?: number
  fromHour?: number
  toHour?: number
  /** Gesetzt heißt Bearbeitungsmodus; verändert wird immer das erste nicht-ghost Band. */
  onChange?: (rules: ScheduleRule[]) => void
  snapMinutes?: number
}) {
  const hours = toHour - fromHour
  const height = hours * rowHeight
  const [draft, setDraft] = useState<{ day: number; from: number; to: number } | null>(null)
  const dragging = useRef<{ day: number; anchor: number } | null>(null)

  const editable = typeof onChange === 'function'
  const editedBand = bands.find(band => !band.ghost && !band.clipped)
  const blocks = toBlocks(bands)

  const snap = (offsetY: number): number => {
    const raw = (offsetY / rowHeight + fromHour) * 60
    const clamped = Math.max(0, Math.min(MINUTES_PER_DAY, raw))
    return Math.round(clamped / snapMinutes) * snapMinutes
  }

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>, day: number) => {
    if (!editable) return
    if ((event.target as HTMLElement).classList.contains('sched-block')) return

    const rect = event.currentTarget.getBoundingClientRect()
    const anchor = snap(event.clientY - rect.top)
    dragging.current = { day, anchor }
    setDraft({ day, from: anchor, to: anchor })
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragging.current
    if (!drag) return

    const rect = event.currentTarget.getBoundingClientRect()
    const current = snap(event.clientY - rect.top)
    setDraft({
      day: drag.day,
      from: Math.min(drag.anchor, current),
      to: Math.max(drag.anchor, current),
    })
  }

  const handlePointerUp = () => {
    const drag = dragging.current
    const current = draft
    dragging.current = null
    setDraft(null)

    if (!drag || !current || !editedBand || !onChange) return
    if (current.to - current.from < snapMinutes) return

    onChange([
      ...editedBand.rules,
      { dayMask: 1 << current.day, startMinute: current.from, endMinute: current.to },
    ])
  }

  /**
   * Klick auf einen Block entfernt diesen Tag aus der Regel. Verschwindet die Regel dadurch
   * ganz, fällt sie weg — eine Regel kann über eine Tagesmaske mehrere Tage abdecken.
   */
  const removeBlock = (block: Block) => {
    if (!editable || !editedBand || !onChange || block.band !== editedBand) return

    const next: ScheduleRule[] = []
    for (const rule of editedBand.rules) {
      const matches = (rule.dayMask & (1 << block.day))
        && rule.startMinute === block.from && rule.endMinute === block.to

      if (!matches) {
        next.push(rule)
        continue
      }

      const reduced = rule.dayMask & ~(1 << block.day)
      if (reduced !== 0) next.push({ ...rule, dayMask: reduced })
    }
    onChange(next)
  }

  const columns: React.ReactNode[] = []
  for (let day = 0; day < 7; day++) {
    const lines: React.ReactNode[] = []
    for (let hour = fromHour; hour < toHour; hour++) {
      lines.push(<div key={`h${hour}`} className="hline"
                      style={{ top: (hour - fromHour) * rowHeight }} />)
      if (rowHeight >= 22) {
        lines.push(<div key={`hh${hour}`} className="hline half"
                        style={{ top: (hour - fromHour + 0.5) * rowHeight }} />)
      }
    }

    const dayBlocks = blocks.filter(block => block.day === day)

    columns.push(
      <div
        key={day}
        className={`sched-col${editable ? ' editable' : ''}`}
        style={{ height }}
        onPointerDown={event => handlePointerDown(event, day)}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {lines}
        {dayBlocks.map((block, blockIndex) => {
          const top = (block.from / 60 - fromHour) * rowHeight
          const blockHeight = Math.max(3, ((block.to - block.from) / 60) * rowHeight)
          const className = block.band.clipped ? 'sched-block clipped'
            : block.band.ghost ? 'sched-block ghost'
            : 'sched-block'

          return (
            <div
              key={`${block.index}-${blockIndex}`}
              className={className}
              style={{
                top, height: blockHeight,
                ...(block.band.ghost || block.band.clipped ? {} : { color: block.band.color }),
              }}
              title={`${block.band.label ? `${block.band.label} · ` : ''}`
                     + `${de.schedule.days[day]} ${formatMinute(block.from)}–${formatMinute(block.to)}`}
              onClick={event => { event.stopPropagation(); removeBlock(block) }}
            >
              {blockHeight >= 30 && !block.band.ghost && (
                <div className="lbl">{formatMinute(block.from)}<br />{formatMinute(block.to)}</div>
              )}
            </div>
          )
        })}
        {draft && draft.day === day && editedBand && (
          <div className="sched-block" style={{
            color: editedBand.color,
            top: (draft.from / 60 - fromHour) * rowHeight,
            height: Math.max(2, ((draft.to - draft.from) / 60) * rowHeight),
          }} />
        )}
      </div>,
    )
  }

  const hourLabels: React.ReactNode[] = []
  for (let hour = fromHour; hour <= toHour; hour++) {
    hourLabels.push(
      <span key={hour} style={{ top: (hour - fromHour) * rowHeight }}>
        {String(hour).padStart(2, '0')}
      </span>,
    )
  }

  return (
    <div className="sched">
      <div className="sched-head">
        <div />
        {de.schedule.days.map(day => <div key={day}>{day}</div>)}
      </div>
      <div className="sched-body">
        <div className="sched-hours" style={{ height }}>{hourLabels}</div>
        {columns}
      </div>
      {bands.filter(band => band.label && !band.ghost).length > 0 && (
        <div className="sched-legend">
          {bands.filter(band => band.label).map((band, index) => (
            <span key={index}>
              <i style={{
                background: band.clipped ? 'var(--danger)'
                  : band.ghost ? 'var(--surface-2)' : band.color,
              }} />
              {band.label}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Die Liste der Zeitfenster. Gleichwertiger Pfad zum Gitter: tastatur- und
 * screenreaderbedienbar und für exakte Uhrzeiten schneller.
 */
export function ScheduleRuleList({ rules, onChange }: {
  rules: ScheduleRule[]
  onChange: (rules: ScheduleRule[]) => void
}) {
  const [day, setDay] = useState(0)
  const [from, setFrom] = useState('08:00')
  const [to, setTo] = useState('16:00')

  const parse = (value: string): number | null => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
    if (!match) return null
    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 24 || minutes > 59 || hours * 60 + minutes > MINUTES_PER_DAY) return null
    return hours * 60 + minutes
  }

  const add = () => {
    const start = parse(from)
    const end = parse(to)
    if (start === null || end === null || start === end) return
    // Fenster über Mitternacht nimmt der Server an und zerlegt sie in zwei Regeln.
    onChange([...rules, { dayMask: 1 << day, startMinute: start, endMinute: end }])
  }

  const remove = (index: number) => onChange(rules.filter((_, i) => i !== index))

  const spreadToWeekdays = (index: number) => {
    const rule = rules[index]
    if (!rule) return
    onChange(rules.map((current, i) => i === index ? { ...current, dayMask: 0b0011111 } : current))
  }

  return (
    <div className="rule-list">
      {rules.length === 0 && <div className="faint sm">{de.groups.noWindow}</div>}

      {rules.map((rule, index) => (
        <div className="rule-row" key={index}>
          <span className="mono">{describeRule(rule)}</span>
          <button type="button" className="btn btn-ghost" style={{ height: 26 }}
                  onClick={() => spreadToWeekdays(index)} title="Mo–Fr">⧉ Mo–Fr</button>
          <button type="button" className="btn btn-ghost btn-icon" style={{ height: 26, width: 26 }}
                  onClick={() => remove(index)} aria-label={de.app.delete}>×</button>
        </div>
      ))}

      <div className="rule-row" style={{ marginTop: 'var(--sp-2)' }}>
        <select value={day} onChange={event => setDay(Number(event.target.value))}
                style={{ width: 'auto' }} aria-label="Wochentag">
          {de.schedule.days.map((name, index) => (
            <option key={name} value={index}>{name}</option>
          ))}
        </select>
        <input type="text" value={from} onChange={event => setFrom(event.target.value)}
               className="mono" style={{ width: 70 }} aria-label="von" />
        <span className="faint">–</span>
        <input type="text" value={to} onChange={event => setTo(event.target.value)}
               className="mono" style={{ width: 70 }} aria-label="bis" />
        <button type="button" className="btn btn-ghost" style={{ height: 26 }} onClick={add}>
          {de.groups.addWindow}
        </button>
      </div>

      <div className="help">{de.schedule.midnightSplit}</div>
    </div>
  )
}

/** Miniatur für Gruppenkarten. */
export function ScheduleMini({ rules, color, isAlwaysOpen }: {
  rules: ScheduleRule[]
  color: string
  isAlwaysOpen: boolean
}) {
  const effective = isAlwaysOpen ? [alwaysRule()] : rules

  return (
    <>
      <div className="mini">
        {Array.from({ length: 7 }, (_, day) => (
          <div key={day} style={{ color }}>
            {effective.filter(rule => rule.dayMask & (1 << day)).map((rule, index) => (
              <i key={index} style={{
                top: `${(rule.startMinute / MINUTES_PER_DAY) * 100}%`,
                height: `${Math.max(6, ((rule.endMinute - rule.startMinute) / MINUTES_PER_DAY) * 100)}%`,
              }} />
            ))}
          </div>
        ))}
      </div>
      <div className="mini-days">
        {de.schedule.days.map(day => <span key={day}>{day}</span>)}
      </div>
    </>
  )
}

export function summarizeSchedule(rules: ScheduleRule[], isAlwaysOpen: boolean): string {
  if (isAlwaysOpen) return de.groups.always
  if (rules.length === 0) return de.groups.noWindow
  return rules.map(describeRule).join(' · ')
}
